import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { initialState } from "./initialState";
import {
  NotificationState,
  ReportTimeTicket,
  NotificationItem,
  NotificationReadStatusEnum,
  NotificationMuteDurationEnum,
} from "app/containers/Features/containers/Notification/types";
import { IPagination } from "app/types";
import { updateFilteredNotifications } from "app/containers/Features/containers/Notification/helpers";
import { isValid, parseISO } from "date-fns";

export const notificationSlice = createSlice({
  name: "notificationState",
  initialState,
  reducers: {
    fetchNotifications: (state: NotificationState) => {
      state.isNotificationsLoading = true;
    },
    getNotificationCount: (state: NotificationState) => {
      state.isNotificationCountLoading = true;
    },
    setNotificationCount: (
      state: NotificationState,
      action: PayloadAction<number>,
    ) => {
      state.notificationCount = action.payload;
      state.isNotificationCountLoading = false;
    },
    setNotifications: (
      state: NotificationState,
      action: PayloadAction<NotificationItem[]>,
    ) => {
      if (state.notificationsPagination.page === 1) {
        state.notifications = action.payload;
      } else {
        const existingIds = new Set(state.notifications.map((n) => n.id));
        const nonDuplicateNew = action.payload.filter(
          (n) => !existingIds.has(n.id),
        );
        state.notifications = [...state.notifications, ...nonDuplicateNew];
      }
      updateFilteredNotifications(state);
      state.isNotificationsLoading = false;
    },
    setNotificationsPagination: (
      state: NotificationState,
      action: PayloadAction<IPagination>,
    ) => {
      state.notificationsPagination = action.payload;
    },
    setNotificationsLoading: (
      state: NotificationState,
      action: PayloadAction<boolean>,
    ) => {
      state.isNotificationsLoading = action.payload;
    },
    muteNotifications: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.isMuted = true;
      state.muteDuration = action.payload;
    },
    unmuteNotifications: (state: NotificationState) => {
      state.isMuted = false;
      state.muteDuration = null;
    },
    setMuteState: (
      state: NotificationState,
      action: PayloadAction<string | null>,
    ) => {
      const mutedTill = action.payload;
      const enumValues = Object.values(
        NotificationMuteDurationEnum,
      ) as string[];
      const isEnumValue = !!mutedTill && enumValues.includes(mutedTill);
      const mutedDate =
        mutedTill && !isEnumValue ? parseISO(mutedTill) : null;
      const isTimedMuteActive =
        !!mutedDate && isValid(mutedDate) && mutedDate.getTime() > Date.now();

      if (isEnumValue || isTimedMuteActive) {
        state.isMuted = true;
        state.muteDuration = mutedTill;
      } else {
        state.isMuted = false;
        state.muteDuration = null;
      }
    },
    setActiveTab: (state: NotificationState, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
      state.notificationsPagination.page = 1;
      updateFilteredNotifications(state);
    },
    toggleReadStatus: (
      state: NotificationState,
      action: PayloadAction<{ action: NotificationReadStatusEnum }>,
    ) => {
      // Intentionally empty or handle via markAllAsRead
    },
    toggleSingleReadStatus: (
      state: NotificationState,
      action: PayloadAction<{
        notificationId: string;
        action: NotificationReadStatusEnum;
      }>,
    ) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload.notificationId,
      );
      if (notification) {
        notification.isUnread = action.payload.action === NotificationReadStatusEnum.READ ? false : true;
        updateFilteredNotifications(state);
      }
    },
    archiveNotification: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload,
      );
      if (notification) {
        notification.isArchived = true;
        updateFilteredNotifications(state);
      }
    },
    markAllAsRead: (state: NotificationState) => {
      state.notifications.forEach((n) => {
        n.isUnread = false;
      });
      updateFilteredNotifications(state);
    },
    markAllAsUnread: (state: NotificationState) => {
      state.notifications.forEach((n) => {
        n.isUnread = true;
      });
      updateFilteredNotifications(state);
    },
    archiveAllNotifications: (state: NotificationState) => {
      state.notifications.forEach((n) => {
        n.isArchived = true;
      });
      updateFilteredNotifications(state);
    },
    setReportTimeTickets: (
      state: NotificationState,
      action: PayloadAction<ReportTimeTicket[]>,
    ) => {
      state.reportTimeTickets = action.payload;
    },
    setTicketsPagination: (
      state: NotificationState,
      action: PayloadAction<IPagination>,
    ) => {
      state.ticketsPagination = action.payload;
    },
    setReportTimeSearchTerm: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.reportTimeSearchTerm = action.payload;
    },
    setReportTimeShowDropdown: (
      state: NotificationState,
      action: PayloadAction<boolean>,
    ) => {
      state.reportTimeShowDropdown = action.payload;
    },
    setReportTimeSelectedTicketIds: (
      state: NotificationState,
      action: PayloadAction<string[]>,
    ) => {
      state.reportTimeSelectedTicketIds = action.payload;
    },
    setReportTimeTicketTime: (
      state: NotificationState,
      action: PayloadAction<{ id: string; time: string }>,
    ) => {
      state.reportTimeTimes[action.payload.id] = action.payload.time;
    },
    setSprintRetrospectiveStrongPoint: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.sprintRetrospectiveStrongPoint = action.payload;
    },
    setSprintRetrospectiveFailedPoint: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.sprintRetrospectiveFailedPoint = action.payload;
    },
    setDayOffType: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.dayOffType = action.payload;
    },
    setDayOffComment: (
      state: NotificationState,
      action: PayloadAction<string>,
    ) => {
      state.dayOffComment = action.payload;
    },
  },
});

export const {
  actions,
  reducer: notificationReducer,
  name: notificationSliceKey,
} = notificationSlice;
